import { useEffect, useState, useRef, Component, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import axios from "axios";

// Contexts
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SchoolProvider } from "./contexts/SchoolContext";
import { ThemeProvider } from "./contexts/ThemeContext";

// Pages (public marketing/legal pages stay eager for instant first paint & SEO)
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
// Authenticated & heavy app pages: lazy-loaded (code splitting) so public pages don't download the whole app
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CommandCenter = lazy(() => import("./pages/CommandCenter"));
const Student360 = lazy(() => import("./pages/Student360"));
const SharedContent = lazy(() => import("./pages/SharedContent"));
const Rubrics = lazy(() => import("./pages/Rubrics"));
const PlannerList = lazy(() => import("./pages/PlannerList"));
const LessonPlanner = lazy(() => import("./pages/LessonPlanner"));
const Templates = lazy(() => import("./pages/Templates"));
const Attendance = lazy(() => import("./pages/Attendance"));
const AttendanceReports = lazy(() => import("./pages/AttendanceReports"));
const Gradebook = lazy(() => import("./pages/Gradebook"));
const GradebookReports = lazy(() => import("./pages/GradebookReports"));
const Classes = lazy(() => import("./pages/Classes"));
const Settings = lazy(() => import("./pages/Settings"));
const SubstitutePacket = lazy(() => import("./pages/SubstitutePacket"));
import Pricing from "./pages/Pricing";
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const ParentPortal = lazy(() => import("./pages/ParentPortal"));
const SetupAdmin = lazy(() => import("./pages/SetupAdmin"));
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Help from "./pages/Help";
import TermsOfUse from "./pages/TermsOfUse";
import CookiesPolicy from "./pages/CookiesPolicy";
import Accessibility from "./pages/Accessibility";
const AIAssistant = lazy(() => import("./pages/AIAssistant"));
const PresentationCreator = lazy(() => import("./pages/PresentationCreator"));
const ReportCards = lazy(() => import("./pages/ReportCards"));
const AdaptiveLearning = lazy(() => import("./pages/AdaptiveLearning"));
const StudentProgress = lazy(() => import("./pages/StudentProgress"));
const HomeschoolPortal = lazy(() => import("./pages/HomeschoolPortal"));
const GamesCreator = lazy(() => import("./pages/GamesCreator"));
const StudentLearning = lazy(() => import("./pages/StudentLearning"));
const PlayGame = lazy(() => import("./pages/PlayGame"));
// Play to Learn (Kahoot-style) Pages
const PlayToLearnLanding = lazy(() => import("./pages/PlayToLearnLanding"));
const PlayToLearnGame = lazy(() => import("./pages/PlayToLearnGame"));
const PlayToLearnTeacher = lazy(() => import("./pages/PlayToLearnTeacher"));
const PlayToLearnHost = lazy(() => import("./pages/PlayToLearnHost"));
// Public SEO Marketing Pages
import FeaturesPage from "./pages/FeaturesPage";
import AIAssistantMarketing from "./pages/AIAssistantMarketing";
import HomeschoolMarketing from "./pages/HomeschoolMarketing";
import IntegrationsPage from "./pages/IntegrationsPage";
// Auth Page
import Auth from "./pages/Auth";
// AI Grading System
const AIGrading = lazy(() => import("./pages/AIGrading"));
const StudentAssignment = lazy(() => import("./pages/StudentAssignment"));
// Trust Center & Compliance Pages
import TrustCenter from "./pages/TrustCenter";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import DPA from "./pages/DPA";
import FERPA from "./pages/FERPA";
import COPPA from "./pages/COPPA";
import GDPR from "./pages/GDPR";
import SLA from "./pages/SLA";
import SecurityWhitepaper from "./pages/SecurityWhitepaper";
import AIGovernance from "./pages/AIGovernance";
import IncidentResponse from "./pages/IncidentResponse";
import BreachPolicy from "./pages/BreachPolicy";
import BusinessContinuity from "./pages/BusinessContinuity";
import VendorManagement from "./pages/VendorManagement";
import AcceptableUse from "./pages/AcceptableUse";
import SOC2 from "./pages/SOC2";
import EnterpriseOverview from "./pages/EnterpriseOverview";
// ScrollToTop for route changes
import ScrollToTop from "./components/ScrollToTop";
// SEO: per-route titles/descriptions/canonical
import SeoManager from "./components/SeoManager";
// Leads Dashboard (Admin)
const LeadsDashboard = lazy(() => import("./pages/LeadsDashboard"));
// Chatbot Widget for marketing pages
import ChatbotWidget from "./components/ChatbotWidget";
// Prep Agent - Autonomous Weekly Prep
const PrepAgent = lazy(() => import("./pages/PrepAgent"));
// Feature Detail Pages
import LessonPlanningFeature from "./pages/features/LessonPlanningFeature";
import GradebookFeature from "./pages/features/GradebookFeature";
import AttendanceFeature from "./pages/features/AttendanceFeature";
import AIToolsFeature from "./pages/features/AIToolsFeature";
import PresentationsFeature from "./pages/features/PresentationsFeature";
import WorkflowFeature from "./pages/features/WorkflowFeature";
// Use Case Pages
import ClassroomTeachersUseCase from "./pages/use-cases/ClassroomTeachersUseCase";
import HomeschoolUseCase from "./pages/use-cases/HomeschoolUseCase";
import TutorsUseCase from "./pages/use-cases/TutorsUseCase";
import SchoolsUseCase from "./pages/use-cases/SchoolsUseCase";

// Loading fallback while a lazy page chunk is fetched
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="w-10 h-10 border-4 border-lime-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const API = `${window.location.origin}/api`;

// Remove platform badge
const removePlatformBadge = () => {
  const badge = document.getElementById('emergent-badge');
  if (badge) {
    badge.remove();
  }
};

// Set up observer to catch dynamically injected badge
if (typeof window !== 'undefined') {
  // Remove immediately if exists
  removePlatformBadge();
  
  // Watch for badge being added
  const observer = new MutationObserver((mutations) => {
    mutations.forEach(() => {
      removePlatformBadge();
    });
  });
  
  // Start observing when DOM is ready
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
      removePlatformBadge();
    });
  }
  
  // Also remove on load
  window.addEventListener('load', removePlatformBadge);
}

// Error Boundary to catch component crashes and show recovery UI instead of blank screen
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error Boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: 'system-ui, sans-serif' }}>
          <div style={{ textAlign: 'center', padding: '2rem', maxWidth: '400px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>!</div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e293b', marginBottom: '0.5rem' }}>Something went wrong</h2>
            <p style={{ color: '#64748b', marginBottom: '1.5rem', fontSize: '0.875rem' }}>The page encountered an error. Please reload to continue.</p>
            <button
              onClick={() => window.location.reload()}
              style={{ background: '#0f766e', color: 'white', border: 'none', padding: '0.625rem 1.5rem', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Protected Route component - does NOT re-verify auth on every navigation if user is already authenticated
const ProtectedRoute = ({ children, requireSubscription = true }) => {
  const { user, loading, checkAuth } = useAuth();
  const [isChecking, setIsChecking] = useState(!user);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    // If user is already authenticated (from login or initial checkAuth), skip re-verification
    if (user) {
      setIsChecking(false);
      return;
    }

    // Only verify with API when user is not yet in context
    const verify = async () => {
      await checkAuth();
      setIsChecking(false);
    };
    verify();
  }, [user, checkAuth]);

  // Check subscription status after user is verified
  useEffect(() => {
    const checkSubscription = async () => {
      if (!user || !requireSubscription) {
        setSubLoading(false);
        return;
      }
      
      try {
        const res = await axios.get(`${API}/subscription/status`, { withCredentials: true });
        setSubscriptionStatus(res.data);
      } catch (error) {
        console.error('Error checking subscription:', error);
        // On error, assume access to avoid blocking navigation
        setSubscriptionStatus({ has_access: true, status: 'unknown' });
      } finally {
        setSubLoading(false);
      }
    };
    
    if (user) {
      checkSubscription();
    }
  }, [user, requireSubscription]);

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center paper-bg">
        <div className="text-center space-y-4">
          <div className="h-12 w-12 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Check subscription access (only if requireSubscription is true)
  if (requireSubscription && !subLoading && subscriptionStatus && !subscriptionStatus.has_access) {
    // Redirect to pricing page if trial expired or no subscription
    return <Navigate to="/pricing" state={{ trialExpired: subscriptionStatus.status === 'trial_expired' }} replace />;
  }

  // Still loading subscription status
  if (requireSubscription && subLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center paper-bg">
        <div className="text-center space-y-4">
          <div className="h-12 w-12 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto" />
          <p className="text-slate-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  return children;
};

// App Router component to handle session_id detection
const MARKETING_PATHS = ['/', '/auth', '/features', '/pricing', '/contact', '/trust', '/homeschool', '/ai-features', '/integrations', '/enterprise-overview', '/help'];

const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const showChatbot = MARKETING_PATHS.includes(location.pathname) ||
    location.pathname.startsWith('/features/') ||
    location.pathname.startsWith('/use-cases/');

  return (
    <>
    <ScrollToTop />
    <SeoManager />
    <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={
        <ProtectedRoute><CommandCenter /></ProtectedRoute>
      } />
      <Route path="/planner" element={
        <ProtectedRoute><PlannerList /></ProtectedRoute>
      } />
      <Route path="/planner/new" element={
        <ProtectedRoute><LessonPlanner /></ProtectedRoute>
      } />
      <Route path="/planner/:planId" element={
        <ProtectedRoute><LessonPlanner /></ProtectedRoute>
      } />
      <Route path="/templates" element={
        <ProtectedRoute><Templates /></ProtectedRoute>
      } />
      <Route path="/attendance" element={
        <ProtectedRoute><Attendance /></ProtectedRoute>
      } />
      <Route path="/attendance/reports" element={
        <ProtectedRoute><AttendanceReports /></ProtectedRoute>
      } />
      <Route path="/gradebook" element={
        <ProtectedRoute><Gradebook /></ProtectedRoute>
      } />
      <Route path="/gradebook/reports" element={
        <ProtectedRoute><GradebookReports /></ProtectedRoute>
      } />
      <Route path="/ai-grading" element={
        <ProtectedRoute><AIGrading /></ProtectedRoute>
      } />
      {/* Public Student Assignment Route - No Auth Required */}
      <Route path="/assignment/:token" element={<StudentAssignment />} />
      <Route path="/report-cards" element={
        <ProtectedRoute><ReportCards /></ProtectedRoute>
      } />
      <Route path="/classes" element={
        <ProtectedRoute><Classes /></ProtectedRoute>
      } />
      <Route path="/share/:token" element={<SharedContent />} />
      <Route path="/rubrics" element={
        <ProtectedRoute><Rubrics /></ProtectedRoute>
      } />
      <Route path="/students/:studentId" element={
        <ProtectedRoute><Student360 /></ProtectedRoute>
      } />
      <Route path="/classes/:classId" element={
        <ProtectedRoute><Classes /></ProtectedRoute>
      } />
      <Route path="/substitute-packet" element={
        <ProtectedRoute><SubstitutePacket /></ProtectedRoute>
      } />
      <Route path="/presentations" element={
        <ProtectedRoute><PresentationCreator /></ProtectedRoute>
      } />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/subscription/success" element={
        <ProtectedRoute requireSubscription={false}><SubscriptionSuccess /></ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute requireSubscription={false}><Settings /></ProtectedRoute>
      } />
      <Route path="/ai-assistant" element={
        <ProtectedRoute><AIAssistant /></ProtectedRoute>
      } />
      <Route path="/adaptive-learning" element={
        <ProtectedRoute><AdaptiveLearning /></ProtectedRoute>
      } />
      <Route path="/student-progress" element={
        <ProtectedRoute><StudentProgress /></ProtectedRoute>
      } />
      <Route path="/student-progress/:studentId" element={
        <ProtectedRoute><StudentProgress /></ProtectedRoute>
      } />
      <Route path="/games" element={
        <ProtectedRoute><GamesCreator /></ProtectedRoute>
      } />
      <Route path="/prep-agent" element={
        <ProtectedRoute><PrepAgent /></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute requireSubscription={false}><AdminPanel /></ProtectedRoute>
      } />
      <Route path="/leads" element={
        <ProtectedRoute requireSubscription={false}><LeadsDashboard /></ProtectedRoute>
      } />
      {/* Parent Portal (Public - no auth required) */}
      <Route path="/portal/:token" element={<ParentPortal />} />
      {/* Homeschool Parent Portal (Public - token-based) */}
      <Route path="/homeschool-portal/:token" element={<HomeschoolPortal />} />
      {/* Student Learning Path (Public - token-based) */}
      <Route path="/student-learning/:token" element={<StudentLearning />} />
      {/* Play Game (Public - no auth required) */}
      <Route path="/play-game/:gameId" element={<PlayGame />} />
      {/* Play to Learn - Kahoot-style (Public student routes) */}
      <Route path="/play-to-learn" element={<PlayToLearnLanding />} />
      <Route path="/play-to-learn/game/:sessionId" element={<PlayToLearnGame />} />
      {/* Play to Learn - Teacher routes (Protected) */}
      <Route path="/teacher/play-to-learn" element={
        <ProtectedRoute><PlayToLearnTeacher /></ProtectedRoute>
      } />
      <Route path="/teacher/play-to-learn/host/:sessionId" element={
        <ProtectedRoute><PlayToLearnHost /></ProtectedRoute>
      } />
      {/* Setup Admin (Public - one-time use) */}
      <Route path="/setup-admin" element={<SetupAdmin />} />
      {/* Public SEO Marketing Pages */}
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/ai-features" element={<AIAssistantMarketing />} />
      <Route path="/homeschool" element={<HomeschoolMarketing />} />
      <Route path="/integrations" element={<IntegrationsPage />} />
      {/* Footer/Legal Pages (Public) */}
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/help" element={<Help />} />
      <Route path="/terms-of-use" element={<TermsOfUse />} />
      <Route path="/cookies-policy" element={<CookiesPolicy />} />
      <Route path="/accessibility" element={<Accessibility />} />
      {/* Trust Center & Compliance Pages (Public) */}
      <Route path="/trust" element={<TrustCenter />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/dpa" element={<DPA />} />
      <Route path="/ferpa" element={<FERPA />} />
      <Route path="/coppa" element={<COPPA />} />
      <Route path="/gdpr" element={<GDPR />} />
      <Route path="/sla" element={<SLA />} />
      <Route path="/security-whitepaper" element={<SecurityWhitepaper />} />
      <Route path="/ai-governance" element={<AIGovernance />} />
      <Route path="/incident-response" element={<IncidentResponse />} />
      <Route path="/breach-policy" element={<BreachPolicy />} />
      <Route path="/business-continuity" element={<BusinessContinuity />} />
      <Route path="/vendor-management" element={<VendorManagement />} />
      <Route path="/acceptable-use" element={<AcceptableUse />} />
      <Route path="/soc2" element={<SOC2 />} />
      <Route path="/enterprise-overview" element={<EnterpriseOverview />} />
      {/* Feature Detail Pages (Public) */}
      <Route path="/features/lesson-planning" element={<LessonPlanningFeature />} />
      <Route path="/features/gradebook" element={<GradebookFeature />} />
      <Route path="/features/attendance" element={<AttendanceFeature />} />
      <Route path="/features/ai-tools" element={<AIToolsFeature />} />
      <Route path="/features/presentations" element={<PresentationsFeature />} />
      <Route path="/features/workflow" element={<WorkflowFeature />} />
      {/* Use Case Pages (Public) */}
      <Route path="/use-cases/classroom-teachers" element={<ClassroomTeachersUseCase />} />
      <Route path="/use-cases/homeschool" element={<HomeschoolUseCase />} />
      <Route path="/use-cases/tutors" element={<TutorsUseCase />} />
      <Route path="/use-cases/schools" element={<SchoolsUseCase />} />
      {/* Fallback redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
    </ErrorBoundary>
    {showChatbot && <ChatbotWidget />}
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <SchoolProvider>
              <BrowserRouter>
                <Toaster position="top-right" richColors />
                <AppRouter />
              </BrowserRouter>
            </SchoolProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
